/** Scannable COP31 key-facts block (semantic <dl>), shared by /cop31 and /cop31-guide. */
type Lang = "tr" | "en";

const FACTS: Record<Lang, { title: string; rows: [string, string][]; pending: [string, string][]; note: string }> = {
  en: {
    title: "COP31 at a glance",
    rows: [
      ["Dates", "9–20 November 2026"],
      ["Venue", "Antalya EXPO, Antalya, Türkiye"],
      ["COP President", "Murat Kurum (Türkiye, host)"],
      ["President for Negotiations", "Chris Bowen (Australia)"],
      ["Leaders' Summit", "11–12 November 2026"],
      ["Pre-COP", "5–8 October 2026, Fiji and Tuvalu"],
    ],
    pending: [
      ["Main negotiation tracks", "To be added once the official agenda is published."],
    ],
    note: "Pending",
  },
  tr: {
    title: "Bir bakışta COP31",
    rows: [
      ["Tarih", "9–20 Kasım 2026"],
      ["Yer", "Antalya EXPO, Antalya, Türkiye"],
      ["COP Başkanı", "Murat Kurum (Türkiye, ev sahibi)"],
      ["Müzakerelerden sorumlu başkan", "Chris Bowen (Avustralya)"],
      ["Liderler Zirvesi", "11–12 Kasım 2026"],
      ["Pre-COP", "5–8 Ekim 2026, Fiji ve Tuvalu"],
    ],
    pending: [],
    note: "Beklemede",
  },
};

export function Cop31FactBlock({ lang }: { lang: Lang }) {
  const f = FACTS[lang];
  return (
    <section aria-labelledby="cop31-facts">
      <h2 id="cop31-facts" className="mb-4 font-serif text-2xl leading-tight md:text-3xl">
        {f.title}
      </h2>
      <dl className="max-w-4xl divide-y divide-ink/10 border-y border-ink/10">
        {f.rows.map(([k, v]) => (
          <div key={k} className="grid gap-1 py-3 sm:grid-cols-[11rem_1fr]">
            <dt className="text-[10px] uppercase tracking-[0.2em] text-ink/65">{k}</dt>
            <dd className="text-[15px]">{v}</dd>
          </div>
        ))}
        {f.pending.map(([k, v]) => (
          <div key={k} className="grid gap-1 py-3 sm:grid-cols-[11rem_1fr]" data-placeholder="true">
            <dt className="text-[10px] uppercase tracking-[0.2em] text-ink/65">{k}</dt>
            <dd className="text-sm text-muted-foreground">
              <span className="mr-2 rounded-full bg-muted px-2 py-0.5 text-[11px]">
                {f.note}
              </span>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Extra FAQ entries matching the fact block (for FAQPage JSON-LD). */
export const COP31_FACT_FAQ: Record<Lang, { q: string; a: string }[]> = {
  en: [
    { q: "What are the COP31 dates?", a: "COP31 runs 9–20 November 2026 at Antalya EXPO in Antalya, Türkiye." },
    { q: "Who is the COP31 President for Negotiations?", a: "Chris Bowen of Australia is President for Negotiations; Murat Kurum of host country Türkiye is COP President." },
    { q: "When is the COP31 Leaders' Summit?", a: "The Leaders' Summit takes place 11–12 November 2026." },
    { q: "When and where is the COP31 Pre-COP?", a: "The Pre-COP takes place 5–8 October 2026 in Fiji and Tuvalu." },
  ],
  tr: [
    { q: "COP31 hangi tarihlerde?", a: "COP31, 9–20 Kasım 2026 tarihlerinde Antalya EXPO'da (Antalya, Türkiye) yapılıyor." },
    { q: "COP31'de müzakerelerden sorumlu başkan kim?", a: "Müzakerelerden sorumlu başkan Avustralya'dan Chris Bowen; COP Başkanı ev sahibi Türkiye'den Murat Kurum." },
    { q: "COP31 Liderler Zirvesi ne zaman?", a: "Liderler Zirvesi 11–12 Kasım 2026 tarihlerinde yapılıyor." },
    { q: "COP31 Pre-COP ne zaman ve nerede?", a: "Pre-COP, 5–8 Ekim 2026 tarihlerinde Fiji ve Tuvalu'da yapılıyor." },
  ],
};

export function factFaqEntities(lang: Lang) {
  return COP31_FACT_FAQ[lang].map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));
}
